import React  from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  Button,
  FormLabel,
} from "@mui/material";
import UserLayout from "components/layouts/UserLayout";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";
import { useState ,useEffect} from "react";
import "assets/CSS/Switch.css";
import { DisconnectConnection, getAuthURL, getConnectionStatus } from "../Logic/QuickBooksHandler";
import { keyframes } from '@mui/system';

const QuickBooksDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(false);

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {  
    try {
        setConnectionLoading(true);
       const status = await getConnectionStatus();
       setIsConnected(status.result.isConnected);
       setCompanyInfo(status.result.companyName);
    } catch (error) {
      console.error('Error checking connection status:', error);
    }finally{
      setConnectionLoading(false);
    }
  };

  const handleConnectQuickBooks = async () => {
    setIsConnecting(true);
    try {
      const result = await getAuthURL(navigate);
      
      if (result && result.result) {
        // Open QuickBooks auth in a popup or redirect
        const width = 600;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const authWindow = window.open(
          result.result,
          'QuickBooks Auth',
          `width=${width},height=${height},left=${left},top=${top}`
        );
  
        // Listen for connection success message from callback
        const handleMessage = (event) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'QUICKBOOKS_CONNECTED' && event.data.success) {
            setIsConnected(true);
            checkConnectionStatus(); // Refresh connection data
            if (authWindow) authWindow.close();
            window.removeEventListener('message', handleMessage);
          }
        };
  
        window.addEventListener('message', handleMessage);
        
        // Fallback: check connection status periodically if popup messaging fails
        const fallbackCheck = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(fallbackCheck);
            checkConnectionStatus();
          }
        }, 1000);
  
      } else {
        console.error('Failed to get auth URL:', result.message);
      }
    } catch (error) {
      console.error('Connection error:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm('Are you sure you want to disconnect from QuickBooks?')) {
      try {
        
        await DisconnectConnection();
        setIsConnected(false);
        setCompanyInfo(null);
      } catch (error) {
        console.error('Disconnect error:', error);
      }
    }
  };

  

  // Integration cards data with individual manualSync states
  const [integrationCardsData, setIntegrationCardsData] = useState([
    {
      id: 1,
      title: "Financial Mapping",
      description: "Chart of Accounts mapping",
      icon: "solar:card-outline",
      syncStatus: "2 hours ago",
      totalRequests: "Total Requests",
      syncedStatus: "Synced",
      statusColor: "#23A26D",
      statusBgColor: "#E8F5E9",
      direction: "Cleri → QuickBooks",
      manualSync: false,
      route: "/quickbooks/financialmapping"
    },
    {
      id: 2,
      title: "Customer & Jobsites",
      description: "Customer and Location data",
      icon: "stash:user-id",
      syncStatus: "2 hours ago",
      totalRequests: "Total Requests",
      syncedStatus: "Error",
      statusColor: "#D80027",
      statusBgColor: "#FFEBEE",
      direction: "Cleri → QuickBooks",
      route: "/quickbooks/customer&jobsites",
      errorCount: 2,
      manualSync: false,
    },
    {
      id: 3,
      title: "Employees",
      description: "Employee and payroll data",
      icon: "solar:user-circle-linear",
      syncStatus: "2 hours ago",
      totalRequests: "Total Requests",
      syncedStatus: "Pending",
      statusColor: "#DD992F",
      statusBgColor: "#FFF3E0",
      direction: "Two-way sync",
      manualSync: false,
        route: "/quickbooks/employees"
    },
    {
      id: 4,
      title: "Inventory",
      description: "Materials and assets",
      icon: "solar:box-linear",
      syncStatus: "2 hours ago",
      totalRequests: "Total Requests",
      syncedStatus: "Synced",
      statusColor: "#23A26D",
      statusBgColor: "#E8F5E9",
      direction: "Cleri → QuickBooks",
      route: "/quickbooks/inventory",
      manualSync: false,
    },
    {
      id: 5,
      title: "Services",
      description: "Service items and pricing",
      icon: "solar:settings-linear",
      syncStatus: "2 hours ago",
      totalRequests: "Total Requests",
      syncedStatus: "Synced",
      statusColor: "#23A26D",
      statusBgColor: "#E8F5E9",
      direction: "Cleri → QuickBooks",
      manualSync: false,
        route: "/quickbooks/services",
    },
    {
      id: 6,
      title: "Invoices",
      description: "Assessments and work orders",
      icon: "iconamoon:invoice-light",
      syncStatus: "2 hours ago",
      totalRequests: "Total Requests",
      syncedStatus: "Pending",
      statusColor: "#DD992F",
      statusBgColor: "#FFF3E0",
      direction: "Cleri → QuickBooks",
      manualSync: false,
        route: "/quickbooks/invoices",
      errorCount: 2,
    },
    {
      id: 7,
      title: "Payment",
      description: "Payment record and status",
      icon: "healthicons:money-bag-outline",
      syncStatus: "2 hours ago",
      totalRequests: "Total Requests",
      syncedStatus: "Synced",
      statusColor: "#23A26D",
      statusBgColor: "#E8F5E9",
      direction: "Cleri → QuickBooks",
      manualSync: false,
        route: "/quickbooks/payments"
    },
    {
      id: 8,
      title: "Refunds",
      description: "Credit memos and refunds",
      icon: "tabler:arrow-back-up",
      syncStatus: "2 hours ago",
      totalRequests: "Total Requests",
      syncedStatus: "Synced",
      statusColor: "#23A26D",
      statusBgColor: "#E8F5E9",
      direction: "Cleri → QuickBooks",
      manualSync: false,
        route: "/quickbooks/refunds"
    },
    // {
    //   id: 9,
    //   title: "Terms & Conditions",
    //   description: "Invoice terms and conditions",
    //   icon: "solar:document-linear",
    //   syncStatus: "2 hours ago",
    //   totalRequests: "Total Requests",
    //   syncedStatus: "Synced",
    //   statusColor: "#23A26D",
    //   statusBgColor: "#E8F5E9",
    //   direction: "Cleri → QuickBooks",
    //   manualSync: false,
    //   //   route: "/terms"
    // },
    {
      id: 10,
      title: "Taxes",
      description: "Tax codes and rates",
      icon: "heroicons-outline:receipt-tax",
      syncStatus: "2 hours ago",
      totalRequests: "Total Requests",
      syncedStatus: "Pending",
      statusColor: "#DD992F",
      statusBgColor: "#FFF3E0",
      direction: "Cleri → QuickBooks",
      manualSync: false,
        route: "/quickbooks/taxes",
      errorCount: 2,
    },
  ]);

  // Handler to update manual sync for a specific card
  const handleManualSyncChange = (cardId, checked) => {
    setIntegrationCardsData(prevCards => 
      prevCards.map(card => 
        card.id === cardId ? { ...card, manualSync: checked } : card
      )
    );
  };

  const topCardsData = [
    {
      id: 1,
      value: "30 mins ago",
      label: "Last sync",
      icon: "meteor-icons:badge-check",
      iconColor: "#23A26D",
      bgColor: "#E8F5E9",
    },
    {
      id: 2,
      value: "In 30 mins",
      label: "Next Auto-Sync",
      icon: "tabler:clock",
      iconColor: "#003A70",
      bgColor: "#f0f0f1",
    },
    {
      id: 3,
      value: "8",
      label: "Total Errors",
      icon: "solar:danger-triangle-outline",
      iconColor: "#d32f2f",
      bgColor: "#ffebee",
    },
  ];

  const syncActivityData = [
    {
      category: "Inventory",
      activity: "Synced 45 items",
      time: "30 minutes ago",
      status: "success",
    },
    {
      category: "Services",
      activity: "Synced 12 Services",
      time: "45 minutes ago",
      status: "success",
    },
    {
      category: "Invoices",
      activity: "Failed to synced 3 invoices",
      time: "1 hour ago",
      status: "error",
    },
    {
      category: "Customers",
      activity: "Synced 8 customers",
      time: "1 hour ago",
      status: "success",
    },
  ];

  const handleCardClick = (route) => {
    if (!isConnected) {
      alert('Please connect QuickBooks first');
      return;
    }
    
    if (route) {
      navigate(route);
    }
  };



const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`;

  // Custom color styles
  const iconBgColor = "#f0f0f1";

  return (
    <UserLayout contentHeightFixed={false} activeLink="QuickBooks">
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        {/* Main Container with Single Rounded Box */}
        <Card
          sx={{
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Header Section with Buttons */}
            <Box
              sx={{
                display: isConnected? "flex" : "none",
                gap: 2,
                mb: 4,
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
              
            >
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                onClick={() => navigate('/quickbooks/synclogs')}
                startIcon={
                  <Icon icon="fluent-mdl2:error-badge" width={20} height={20} />
                }
              >
                Fix Errors
              </Button>
              
              <Button
                variant="contained"
                color="main"
                size="medium"
                startIcon={
                  <Icon icon="heroicons:arrow-path" width={20} height={20} />
                }
              >
                Connected
              </Button>
            </Box>

            {/* Top Section - 3 Cards with Icons */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {topCardsData.map((card) => (
                  <Grid item xs={12} sm={4} key={card.id}>
                    <Box
                      sx={{
                        backgroundColor: "background.paper",
                        p: 5,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        minHeight: 150,
                      }}
                    >
                      {/* Icon on the left */}
                      <Box
                        sx={{
                          display: "inline-flex",
                          backgroundColor: card.bgColor,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={card.icon}
                          width={24}
                          height={24}
                          style={{ color: card.iconColor }}
                        />
                      </Box>

                    
                      <Box sx={{ textAlign: "right", flex: 1, ml: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {card.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
            {connectionLoading ?  (
              <Box
              sx={{
                p: 4,
                borderRadius: 2,
                mb: 4,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                {/* Status Chip Skeleton */}
                <Box
                  sx={{
                    backgroundColor: "grey.200",
                    borderRadius: "12px",
                    height: 24,
                    width: 120,
                    mb: 1.5,
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
                
                {/* Title Skeleton */}
                <Box
                  sx={{
                    backgroundColor: "grey.200",
                    borderRadius: 1,
                    height: 32,
                    width: "70%",
                    mb: 1.5,
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
                
                {/* Description Skeleton */}
                <Box
                  sx={{
                    backgroundColor: "grey.200",
                    borderRadius: 1,
                    height: 20,
                    width: "90%",
                    mb: 0.5,
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
                <Box
                  sx={{
                    backgroundColor: "grey.200",
                    borderRadius: 1,
                    height: 20,
                    width: "80%",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
              </Box>
            
              {/* Button Area Skeleton (if you have buttons/actions on the right) */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "grey.200",
                    borderRadius: 1,
                    height: 36,
                    width: 100,
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
                <Box
                  sx={{
                    backgroundColor: "grey.200",
                    borderRadius: 1,
                    height: 36,
                    width: 120,
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
              </Box>
            </Box>
            ):(
              <>
              <Box
              sx={{
                p: 4,
                borderRadius: 2,
                mb: 4,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Chip
                  icon={
                    <Icon
                      icon={isConnected ? "hugeicons:checkmark-circle-03" : "hugeicons:close-circle"}
                      width={16}
                      height={16}
                      style={{ color: isConnected ? "#23A26D" : "#D80027" }}
                    />
                  }
                  label={isConnected ? "Connected" : "Disconnected"}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "0.78rem",
                    backgroundColor: isConnected ? "#E8F5E9" : "#FFEBEE",
                    color: isConnected ? "#23A26D" : "#D80027",
                    fontWeight: 600,
                    mb: 1.5,
                    "& .MuiChip-label": {
                      px: 4,
                      pl: 2,
                    },
                  }}
                />
                <Typography variant="h4">
                  QuickBooks Online Connection
                </Typography>
                <Typography variant="body1">
                  {isConnected
                    ? `Connected to QuickBooks Online company: ${companyInfo?.companyName || 'Cleri Construction LLC'}`
                    : "Connect your QuickBooks Online account to sync data"
                  }
                </Typography>
              </Box>

              {/* Dynamic Buttons based on connection state */}
              <Box sx={{ display: "flex", gap: 2 }}>
                {isConnected ? (
                        <>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="medium"
                            startIcon={<Icon icon="hugeicons:view" width={20} height={20} />}
                          >
                            View in QuickBooks
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="medium"
                            startIcon={<Icon icon="material-symbols-light:download" width={20} height={20} />}
                          >
                            Initial Sync
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="medium"
                            startIcon={<Icon icon="hugeicons:logout-01" width={20} height={20} />}
                            onClick={handleDisconnect}
                          >
                            Disconnect
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          size="medium"
                          startIcon={
                            isConnecting ? (
                              <Icon icon="eos-icons:loading" width={20} height={20} />
                            ) : (
                              <Icon icon="hugeicons:connect" width={20} height={20} />
                            )
                          }
                          onClick={handleConnectQuickBooks}
                          disabled={isConnecting}
                        >
                          {isConnecting ? "Connecting..." : "Connect QuickBooks"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {/* Integration Cards Grid */}
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    {integrationCardsData.map((card) => (
                      <Grid item xs={12} sm={6} md={4} key={card.id}>
                        <Box
                          sx={{
                            cursor: card.route ? "pointer" : "default",
                            transition: "all 0.2s ease",
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            p: 4,
                            height: "100%",
                            backgroundColor: "background.paper",
                            "&:hover": {
                              borderColor: card.route ? "primary.main" : "divider",
                              backgroundColor: card.route ? "action.hover" : "background.paper",
                            },
                          }}
                          onClick={() => handleCardClick(card.route)}
                        >
                          {/* Header with Icon */}
                          <Box
                            sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}
                          >
                            <Box
                              sx={{
                                display: "inline-flex",
                                backgroundColor: iconBgColor,
                                borderRadius: "12px",
                                width: 48,
                                height: 48,
                                alignItems: "center",
                                justifyContent: "center",
                                mr: 2,
                                flexShrink: 0,
                              }}
                            >
                              <Icon icon={card.icon} width={24} height={24} />
                            </Box>

                            <Box sx={{ flex: 1 }}>
                              {/* Title + Error Count Row */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 0.5,
                                }}
                              >
                                <Typography variant="h5">{card.title}</Typography>

                                {/* 🔴 Error Badge (Opposite to Title) */}
                                {card.errorCount && (
                                  <Box
                                    sx={{
                                      backgroundColor: "#FFEBEE",
                                      border: "1px solid #FFCDD2",
                                      color: "#D80027",
                                      fontSize: "0.8rem",
                                      fontWeight: 800,
                                      borderRadius: "12px",
                                      px: 1.8,
                                      py: 0.7,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",

                                    }}
                                  >
                                    <Icon
                                      icon="jam:triangle-danger"
                                      width={15}
                                      height={15}
                                    />
                                    {card.errorCount}
                                  </Box>
                                )}
                              </Box>

                              <Typography variant="body1">
                                {card.description}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Data Table-like Structure */}
                          <Box sx={{ mt: 5 }}>
                            {/* Total Requests Row */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1.5,
                                mt: 2,
                              }}
                            >
                              <Typography variant="body1">
                                {card.totalRequests}
                              </Typography>
                              <Chip
                                label={card.syncedStatus}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: "0.78rem",
                                  backgroundColor: card.statusBgColor,
                                  color: card.statusColor,
                                  fontWeight: 650,
                                  "& .MuiChip-label": {
                                    px: 4,
                                  },
                                }}
                              />
                            </Box>

                            {/* Direction Row */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1.5,
                              }}
                            >
                              <Typography variant="body1">Direction</Typography>
                              <Typography variant="body1">
                                {card.direction}
                              </Typography>
                            </Box>

                            {/* Auto Sync Row - FIXED */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1.5,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Typography variant="body1">Auto Sync</Typography>
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={card.manualSync}
                                    onChange={(e) => {
                                      handleManualSyncChange(card.id, e.target.checked);
                                    }}
                                  />
                                  <span className="slider round"></span>
                                </label>
                              </Box>
                            </Box>


                            {/* Last Sync Row */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="body1">Last Sync</Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                              >
                                {card.syncStatus}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </>
            )}            
            {/* Recent Sync Activity Section */}
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              {/* Header with "View all logs" on the same line */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography variant="h5">Recent Sync Activity</Typography>

                <Button variant="outlined" color="primary" size="medium" onClick = { () => navigate('/quickbooks/synclogs')}>
                  View all logs
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {/* Sync Activity List */}
              <Box>
                {syncActivityData.map((activity, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1.5,
                      borderBottom:
                        index < syncActivityData.length - 1
                          ? "1px solid #eee"
                          : "none",
                    }}
                  >
                    {/* Left section */}
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {activity.category}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {activity.activity} | {activity.time}
                      </Typography>
                    </Box>

                    {/* Right section - Status Chip */}
                    <Chip
                      label={
                        activity.status === "success"
                          ? "Success"
                          : activity.status === "error"
                          ? "Error"
                          : "Pending"
                      }
                      size="small"
                      sx={{
                        height: 28,
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        backgroundColor:
                          activity.status === "success"
                            ? "#E8F5E9"
                            : activity.status === "error"
                            ? "#FFEBEE"
                            : "#FFF3E0",
                        color:
                          activity.status === "success"
                            ? "#23A26D"
                            : activity.status === "error"
                            ? "#D80027"
                            : "#DD992F",
                        "& .MuiChip-label": {
                          px: 4,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </UserLayout>
  );
};

export default QuickBooksDashboard;